import { type DatePickerDefaultShortcuts } from "shared/components/common/datepicker/src/constants/shortcuts";
import {
  type CustomReportPrismaSafeUnknown,
  type CustomReportDALBaseProps as CustomReportDALBasePropsNewLocation,
  type ReplaceWhereClauseFilters as ReplaceWhereClauseFiltersNewLocation,
} from "shared/filter-where-clause/CustomReportDALBaseProps";

import {
  type FilterValueDateRange,
  mappedFilterWhereClauseValues,
} from "shared/filter-where-clause/mappedFilterWhereClauseValues";

export interface FilterChoice {
  value: number | string;
  display: string;
  count?: number;
  choices?: FilterChoice[];
  filterKey?: string;
}

export interface AppFilterOption {
  filterKey?: string;
  component?: FilterOptionComponent;
  where?: (
    | FilterOptionWhereConfig
    | FilterOptionWhereConfigDateRange
    | FilterOptionWhereConfigBooleanCopy
  )[];
}

export interface FilterOptionComponent {
  label?: string;
  type?: keyof typeof FilterComponentType;
  choices?: FilterChoice[];
  /**
   * Text for the hover tooltip that sits after the filter's title. Plain text
   * rather than a `ReactNode` because this config is stored as JSON on the
   * report record. Omit it and no info icon is rendered at all.
   */
  toolTip?: string;
  /**
   * Seeded into the filters request when a custom report loads. A date range
   * component populates two request keys (`<filterKey>-from` /
   * `<filterKey>-to`), so its default is a partial `FiltersRequest` that is
   * merged in rather than a single value.
   */
  defaultValue?: FilterRequestTypes | FiltersRequest;
}

export interface FilterOptionWhereConfig {
  type: FilterType;
  path: string;
  arrayPath?: string;
  nonePath?: string;
  copy?: string | Record<string, any>;
  copyPath?: string;
}

export interface FilterOptionWhereConfigBooleanCopy
  extends FilterOptionWhereConfig {
  type: FilterType.BOOLEAN_COPY;
  copy: Record<string, any>;
  copyIfValue?: boolean | string | number;
  copyIfNotValue?: boolean | string | number;
}

export interface FilterOptionWhereConfigDateRange
  extends FilterOptionWhereConfig {
  type: FilterType.DATE_RANGE;
  default?: keyof typeof DatePickerDefaultShortcuts;
  path: string;
  arrayPath: string;
  copy?: Record<string, any>;
  copyPath?: string;
  valueOnSelectPath?: string;
  /**
   * Set when `path` targets a calendar date rather than a real instant, e.g. a
   * date-only form answer stored as "2026-08-21" and cast to timestamptz at UTC
   * midnight by `WorkflowInstancePayloadDateTableView`. The day boundaries are
   * then built in UTC so the comparison stays pure calendar math. Without it the
   * boundaries are built in the server's zone (`TZ`, America/Chicago), which
   * shifts the window forward and matches the following day's records.
   */
  dateOnly?: boolean;
}

export type FilterRequestTypes =
  | string
  | string[]
  | number
  | number[]
  | (string | number)[]
  | null
  | undefined
  | boolean
  | FilterValueDateRange;
export interface FiltersRequest {
  [key: string]: FilterRequestTypes;
}

export enum FilterType {
  JSON = "JSON",
  JSON_DATE = "JSON_DATE",
  JSON_ARRAY = "JSON_ARRAY",
  INT_ARRAY = "INT_ARRAY",
  STRING = "STRING",
  NUMBER = "NUMBER",
  STRING_ARRAY = "STRING_ARRAY",
  DATE = "DATE",
  BOOLEAN = "BOOLEAN",
  BOOLEAN_SPECIAL = "BOOLEAN_SPECIAL",
  BOOLEAN_COPY = "BOOLEAN_COPY",
  NOT_NULL = "NOT_NULL",
  DATE_RANGE = "DATE_RANGE",
}

export enum FilterComponentType {
  none = "none",
  search = "search",
  Date = "Date",
  selectMenuCheckbox = "selectMenuCheckbox",
  selectMenu = "selectMenu",
  checkbox = "checkbox",
}

/**
 * @deprecated Use `mappedFilterWhereClauseValues` instead.
 */
export const DONOTUSEmappedFilterWhereClauseValues = (
  config: AppFilterOption[],
  requestFilters: FiltersRequest = {},
) => {
  const { mappedWhereClause } = mappedFilterWhereClauseValues({
    filtersConfig: config,
    filtersRequest: requestFilters,
  });
  return mappedWhereClause as any;
};

export { mappedFilterWhereClauseValues } from "./mappedFilterWhereClauseValues";

export type { HandleFilterProps } from "./mappedFilterWhereClauseValues";

/**
 * @deprecated Use `ReplaceWhereClauseFilters` from "shared/filter-where-clause/CustomReportDALBaseProps" instead.
 */
export type ReplaceWhereClauseFilters<T> =
  ReplaceWhereClauseFiltersNewLocation<T>;

/**
 * @deprecated Use `CustomReportDALBaseProps` from "shared/filter-where-clause/CustomReportDALBaseProps" instead.
 */
export type CustomReportDALBaseProps<
  T extends CustomReportPrismaSafeUnknown = CustomReportPrismaSafeUnknown,
  V extends CustomReportPrismaSafeUnknown = CustomReportPrismaSafeUnknown,
> = CustomReportDALBasePropsNewLocation<T, V>;
